import { type FormEvent, useState } from 'react'
import { useLocale, useT } from '../i18n'
import { useAuth } from '../lib/auth'
import { type RfqPayload, cleanPort, fmtPrice, submitRfq } from '../lib/rates'

const input =
  'w-full rounded-md border border-border-strong bg-surface-0 px-3 py-2 text-sm text-ink placeholder:text-muted focus:border-signal focus:outline-none'

const fmtEtd = (iso?: string) => {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })
}

export function RfqForm({
  prefill,
  onClearSelected,
}: { prefill?: Partial<RfqPayload>; onClearSelected?: () => void }) {
  const t = useT()
  const locale = useLocale()
  const { session } = useAuth()
  const [status, setStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle')
  // A specific sailing was clicked (Book row / carrier chip) if we were handed a vessel or carrier.
  const picked = prefill?.vessel || prefill?.carrier ? prefill : null
  // Signed-in shoppers shouldn't retype what we already know — prefill name/email from their session.
  const meEmail = session?.user?.email ?? ''
  const meName = (session?.user?.user_metadata?.full_name as string | undefined) ?? ''

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setStatus('sending')
    const fd = new FormData(e.currentTarget)
    const payload: RfqPayload = {
      name: str(fd, 'name'),
      email: str(fd, 'email'),
      company: str(fd, 'company'),
      pol_name: str(fd, 'pol_name') ?? prefill?.pol_name,
      pol_code: prefill?.pol_code,
      pod_name: str(fd, 'pod_name') ?? prefill?.pod_name,
      pod_code: prefill?.pod_code,
      container_type: str(fd, 'container_type'),
      ready_date: str(fd, 'ready_date'),
      commodity: str(fd, 'commodity'),
      incoterm: str(fd, 'incoterm'),
      notes: str(fd, 'notes'),
      locale,
      // The exact sailing the shopper picked (from a Book row or a carrier price chip).
      carrier: prefill?.carrier,
      vessel: prefill?.vessel,
      voyage: prefill?.voyage,
      etd: prefill?.etd,
      sailing_price: prefill?.sailing_price,
    }
    try {
      await submitRfq(payload)
      setStatus('done')
    } catch {
      setStatus('error')
    }
  }

  if (status === 'done') {
    return (
      <div className="rounded-lg border border-signal/40 bg-surface-0 p-6 text-center">
        <div className="font-display text-lg text-signal">{t('rfq.title')}</div>
        <p className="mt-1 text-sm text-muted">{t('rfq.success')}</p>
      </div>
    )
  }

  return (
    <form onSubmit={onSubmit} className="grid gap-3 sm:grid-cols-2">
      {picked && (
        <div className="sm:col-span-2 flex items-start justify-between gap-2 rounded-lg border border-signal/50 bg-signal-soft px-3 py-2.5">
          <div className="min-w-0">
            <div className="mono text-[11px] font-semibold uppercase tracking-wide text-signal">
              Pre-booking this sailing
            </div>
            <div className="mt-0.5 truncate text-sm text-ink">
              {picked.carrier && <span className="font-semibold">{picked.carrier}</span>}
              {picked.vessel && <span> · {picked.vessel}</span>}
              {picked.voyage && <span className="text-muted"> {picked.voyage}</span>}
            </div>
            {(fmtEtd(picked.etd) || picked.sailing_price != null) && (
              <div className="mono mt-0.5 text-xs text-muted tnum">
                {fmtEtd(picked.etd) && <>ETD {fmtEtd(picked.etd)}</>}
                {picked.sailing_price != null && (
                  <>
                    {fmtEtd(picked.etd) ? ' · ' : ''}
                    {(picked.container_type ?? '40HC').toUpperCase()} {fmtPrice(picked.sailing_price)}
                  </>
                )}
              </div>
            )}
          </div>
          {onClearSelected && (
            <button
              type="button"
              onClick={onClearSelected}
              aria-label="Clear selected sailing"
              className="shrink-0 text-lg leading-none text-faint transition-colors hover:text-ink"
            >
              ×
            </button>
          )}
        </div>
      )}
      {/* key by session so these uncontrolled inputs re-sync once auth resolves (async on mount). */}
      <input
        key={`name-${session?.user?.id ?? 'anon'}`}
        className={input}
        name="name"
        defaultValue={meName}
        placeholder={t('rfq.name')}
        autoComplete="name"
      />
      <input
        key={`email-${session?.user?.id ?? 'anon'}`}
        className={input}
        name="email"
        type="email"
        required
        defaultValue={meEmail}
        placeholder={`${t('rfq.email')} *`}
        autoComplete="email"
      />
      <input className={input} name="company" placeholder={t('rfq.company')} />
      <input className={input} name="commodity" placeholder={t('rfq.commodity')} />
      <input
        className={input}
        name="pol_name"
        placeholder={t('rfq.origin')}
        defaultValue={prefill?.pol_name ? cleanPort(prefill.pol_name) : ''}
      />
      <input
        className={input}
        name="pod_name"
        placeholder={t('rfq.destination')}
        defaultValue={prefill?.pod_name ? cleanPort(prefill.pod_name) : ''}
      />
      <input className={input} name="ready_date" placeholder={t('rfq.readyDate')} />
      {/* Container + incoterm — one condensed block of single-click chips (tick, don't open-then-pick).
          Container is the primary choice (full-width segmented row); incoterm is a light, optional pill row. */}
      {/* Key by the chosen container so these uncontrolled radios re-sync when the page container
          switches (clicking a price card) — otherwise a pre-book would submit the stale box. */}
      <fieldset key={`ctr-${prefill?.container_type ?? '40HC'}`} className="sm:col-span-2">
        <legend className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-faint">
          {t('rfq.containerType')}
        </legend>
        <div className="grid grid-cols-4 gap-1.5">
          {(
            [
              ['20GP', '20ft'],
              ['40GP', '40ft'],
              ['40HC', '40HC'],
              ['LCL', 'LCL'],
            ] as const
          ).map(([value, label]) => (
            <label key={value} className="cursor-pointer">
              <input
                type="radio"
                name="container_type"
                value={value}
                defaultChecked={value === (prefill?.container_type ?? '40HC').toUpperCase()}
                className="peer sr-only"
              />
              <span className="mono block rounded-md border border-border-strong py-1.5 text-center text-[13px] text-muted transition-colors hover:border-signal peer-checked:border-signal peer-checked:bg-signal/10 peer-checked:font-semibold peer-checked:text-signal peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-signal">
                {label}
              </span>
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset className="-mt-1 sm:col-span-2">
        <legend className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-faint">
          {t('rfq.incoterm')} <span className="normal-case text-faint/70">· optional</span>
        </legend>
        <div className="flex flex-wrap gap-1.5">
          {(['FOB', 'CIF', 'EXW', 'DDP'] as const).map((ic) => (
            <label key={ic} className="cursor-pointer">
              <input
                type="radio"
                name="incoterm"
                value={ic}
                defaultChecked={ic === 'FOB'}
                className="peer sr-only"
              />
              <span className="mono block rounded-full border border-border-strong px-3 py-1 text-xs text-muted transition-colors hover:border-signal peer-checked:border-signal peer-checked:bg-signal/10 peer-checked:font-semibold peer-checked:text-signal peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-signal">
                {ic}
              </span>
            </label>
          ))}
        </div>
      </fieldset>
      <textarea
        className={`${input} sm:col-span-2`}
        name="notes"
        rows={3}
        placeholder={t('rfq.notes')}
      />
      <button
        type="submit"
        disabled={status === 'sending'}
        className="sm:col-span-2 rounded-md bg-signal px-4 py-2.5 font-semibold text-on-accent hover:opacity-90 disabled:opacity-60"
      >
        {status === 'sending' ? t('rfq.submitting') : t('rfq.submit')}
      </button>
      {status === 'error' && <p className="sm:col-span-2 text-sm text-down">{t('rfq.error')}</p>}
      <p className="sm:col-span-2 text-xs text-muted">{t('rfq.sub')}</p>
    </form>
  )
}

function str(fd: FormData, k: string): string | undefined {
  const v = fd.get(k)
  return typeof v === 'string' && v.trim() ? v.trim() : undefined
}
